import React, { useCallback, useState, useRef, useLayoutEffect, useMemo } from 'react';
import { View, StyleSheet } from 'react-native';
import TouchableBounce from 'utils/packages/TouchableBounce';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import Text from 'design-system/Text';
import StatusBar from 'design-system/StatusBar';
import { rem } from 'design-system/values';
import { Button, CardView, NavigationHeader, Spacer } from 'design-system';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useCommonInvestStyles } from './styles';
import CheckBox from '../components/CheckBox';
import OnboardingTitle from '../components/OnboardingTitle';
const CONDITIONS = [
    {
        id: 'W-8 Disclosures / Certification',
        text: 'I certify that I am not a US citizen, US resident alien or other US person for US tax purposes, and I am submitting the applicable Form W-8 BEN with this form to certify my foreign status and, if applicable, claim tax treaty benefits.',
    },
    {
        id: 'Digital Signature Acknowledgement',
        text: `Under penalties of perjury, I declare that I have examined the information on this form and to the best of my knowledge and belief it is true, correct, and complete. I further certify under penalties of perjury that:
•  I am the individual that is the beneficial owner (or am authorized to sign for the individual that is the beneficial owner) of all the income to which this form relates or am using this form to document myself for chapter 4 purposes,
•  The person named on line 1 of this form is not a U.S. person,
•  The income to which this form relates is: (a) not effectively connected with the conduct of a trade or business in the United States, (b) effectively connected but is not subject to tax under an applicable income tax treaty, or (c) the partner’s share of a partnership’s effectively connected income,
•  The person named on line 1 of this form is a resident of the treaty country listed on line 9 of the form (if any) within the meaning of the income tax treaty between the United States and that country, and
•  For broker transactions or barter exchanges, the beneficial owner is an exempt foreign person as defined in the instructions.`,
        line2: 'Furthermore, I authorize this form to be provided to any withholding agent that has control, receipt, or custody of the income of which I am the beneficial owner or any withholding agent that can disburse or make payments of the income of which I am the beneficial owner. I agree that I will submit a new form within 30 days if any certification made on this form becomes incorrect.',
    },
];
const ConditionsScreen = ({ route: { params }, navigation, navigation: { navigateDeprecated }, }) => {
    const scrollY = useSharedValue(0);
    const colors = useColors();
    const commonStyles = useCommonInvestStyles(params?.mountedOnAnotherScreen);
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTitle: '',
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary, params?.mountedOnAnotherScreen]);
    const mountedOnAnotherScreen = params?.mountedOnAnotherScreen;
    const [state, setState] = useState(() => CONDITIONS.map((item) => ({ ...item, value: false })));
    const stateRef = useRef(state);
    const allChecked = state.map((item) => item.value).every(Boolean);
    const onSubmit = useCallback(() => {
        if (allChecked) {
            navigateDeprecated('InvestBrokerRequirements');
        }
    }, [allChecked]);
    const onChange = useCallback((id, value) => {
        const newState = stateRef.current.map((item) => ({
            ...item,
            value: item.id === id ? value : item.value,
        }));
        setState(newState);
        stateRef.current = newState;
    }, []);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y > 0 ? contentOffset.y : 0;
    });
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const paddingBottom = useMarginBottom('paddingBottom', rem(56 + 16), commonStyles.contentContainer);
    return (<View style={commonStyles.containerNoPadding}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} rightContent={<View style={!!mountedOnAnotherScreen && styles.rightBtnContainer}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView scrollEventThrottle={8} onScroll={scrollHandler} showsVerticalScrollIndicator={false} contentContainerStyle={paddingBottom}>
        <OnboardingTitle for="W8" noShortTitle black fixed scrollY={scrollY}/>
        <Spacer h={8}/>
        {state.map((thisItem, index) => (<React.Fragment key={thisItem.id}>
            <TouchableBounce onPress={() => {
                onChange(thisItem.id, !stateRef.current[index].value);
            }}>
              <CardView withPadding noMarginTop noMarginBottom>
                <View style={styles.row}>
                  <CheckBox value={thisItem.value} onValueChange={(value) => {
                onChange(thisItem.id, value);
            }}/>
                  <View style={styles.itemTextContainer}>
                    <Text TextThin-14>{thisItem.text}</Text>
                    {thisItem.line2 ? (<>
                        <Spacer h={4}/>
                        <Text TextThin-14>{thisItem.line2}</Text>
                      </>) : null}
                  </View>
                </View>
              </CardView>
            </TouchableBounce>
            <Spacer.H16 />
          </React.Fragment>))}
      </Animated.ScrollView>
      <Button brand title="Confirm" onPress={onSubmit} style={[mountedOnAnotherScreen ? commonStyles.mountedButton : commonStyles.button]}/>
    </View>);
};
const styles = StyleSheet.create({
    itemTextContainer: {
        flex: 1,
        marginLeft: rem(16),
    },
    row: {
        flexDirection: 'row',
    },
    rightBtnContainer: {
        flex: 1,
        paddingRight: 15,
        alignItems: 'flex-end',
        justifyContent: 'center',
    },
});
export default ConditionsScreen;
